import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, Button } from '@nimbitlabs/ui';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface BillingReturnProps {
  onVerifyPayment: (reference: string) => Promise<void>;
  onPollSubscription: () => Promise<{ status: string }>;
  settingsPath?: string;
  dashboardPath?: string;
  maxPollAttempts?: number;
  pollIntervalMs?: number;
}

export function BillingReturn({
  onVerifyPayment,
  onPollSubscription,
  settingsPath = '/settings',
  dashboardPath = '/',
  maxPollAttempts = 10,
  pollIntervalMs = 4000,
}: BillingReturnProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing your payment...');

  useEffect(() => {
    const verifySubscription = async () => {
      try {
        // Get the reference from URL params (Paystack/Stripe adds this)
        const reference = searchParams.get('reference') || searchParams.get('trxref');
        
        if (!reference) {
          setStatus('error');
          setMessage('No payment reference found. Please contact support if you were charged.');
          return;
        }

        // First, call verification endpoint to activate subscription
        try {
          await onVerifyPayment(reference);
        } catch (error) {
          console.error('Verification endpoint error:', error);
          // Continue to polling - webhook might have already processed it
        }

        // Poll for subscription activation
        let attempts = 0;
        
        const pollSubscription = async (): Promise<boolean> => {
          attempts++;
          
          try {
            const subscription = await onPollSubscription();
            
            if (subscription && (subscription.status === 'active' || subscription.status === 'pending')) {
              setStatus('success');
              setTimeout(() => navigate(settingsPath), 2000);
              return true;
            } else if (subscription && subscription.status === 'past_due') {
              setStatus('error');
              setMessage('Your payment is past due. Please update your payment method.');
              setTimeout(() => navigate(settingsPath), 5000);
              return true;
            }
            
            // Still waiting for activation
            if (attempts < maxPollAttempts) {
              await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
              return pollSubscription();
            } else {
              // Timeout - webhook might still be processing
              setStatus('error');
              setMessage('Subscription activation is taking longer than expected. Please check your subscription status in settings.');
              setTimeout(() => navigate(settingsPath), 5000);
              return true;
            }
          } catch (error) {
            if (attempts < maxPollAttempts) {
              await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
              return pollSubscription();
            } else {
              throw error;
            }
          }
        };

        await pollSubscription();
        
      } catch (error) {
        console.error('Error verifying subscription:', error);
        setStatus('error');
        setMessage('An error occurred while verifying your subscription. Please check your subscription status in settings.');
        
        setTimeout(() => {
          navigate(settingsPath);
        }, 5000);
      }
    };

    verifySubscription();
  }, [searchParams, navigate, onVerifyPayment, onPollSubscription, settingsPath, maxPollAttempts, pollIntervalMs]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-12 pb-12">
          <div className="flex flex-col items-center text-center space-y-6">
            {status === 'loading' && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl animate-pulse"></div>
                  <div className="relative">
                    <Loader2 className="h-20 w-20 text-primary animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-12 w-12 rounded-full border-4 border-primary/30 animate-pulse"></div>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <h2 className="text-2xl font-bold">Processing Payment</h2>
                  <div className="flex gap-1 justify-center">
                    <div className="h-2 w-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '0ms' }}></div>
                    <div className="h-2 w-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '150ms' }}></div>
                    <div className="h-2 w-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <p className="text-sm text-muted-foreground">This usually takes a few seconds...</p>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl"></div>
                  <CheckCircle className="h-16 w-16 text-green-500 relative" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-green-500">Payment Successful!</h2>
                  <p className="text-muted-foreground">Your subscription is now active!</p>
                  <p className="text-sm text-muted-foreground">Redirecting you to settings...</p>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 bg-destructive/20 rounded-full blur-xl"></div>
                  <AlertCircle className="h-16 w-16 text-destructive relative" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold text-destructive">Verification Issue</h2>
                  <p className="text-muted-foreground">{message}</p>
                  <div className="flex gap-3 justify-center pt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => navigate(settingsPath)}
                    >
                      Go to Settings
                    </Button>
                    <Button 
                      onClick={() => navigate(dashboardPath)}
                    >
                      Go to Dashboard
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default BillingReturn;
